'use client';

import { useState } from 'react';
import { useIsHydrated } from '@/hooks/useIsHydrated';
import Icon from '@/components/ui/AppIcon';
import { subscribeNewsletter } from '@/lib/subscribeNewsletter';
import { track } from '@/lib/analytics';
import { isValidEmail } from '@/lib/validation';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const Intro = () => (
  <>
    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
      <Icon name="EnvelopeIcon" size={32} className="text-primary" />
    </div>
    <h3 className="font-headline text-3xl font-bold text-foreground">Sustainability Updates</h3>
    <p className="font-body text-lg text-muted-foreground">
      Get our latest eco-tips, product news, and sustainability insights in your inbox.
    </p>
  </>
);

const NewsletterSignup = () => {
  const isHydrated = useIsHydrated();
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState(''); // bots fill this; humans never see it
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const value = email.trim();
    if (!isValidEmail(value)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('submitting');
    setMessage('');

    const result = await subscribeNewsletter(value, honeypot);

    if (result.ok) {
      setStatus('success');
      setMessage(result.message);
      setEmail('');
      track('newsletter_signup', { source: 'insights' });
    } else {
      setStatus('error');
      setMessage(result.message);
    }
  };

  const submitting = status === 'submitting';
  const hasError = status === 'error';

  if (!isHydrated) {
    return (
      <div className="bg-surface rounded-lg p-6">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <Intro />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg p-6">
      <div className="max-w-2xl mx-auto text-center space-y-6">
        <Intro />

        {status === 'success' ? (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center gap-2 font-cta font-medium text-success"
          >
            <Icon name="CheckCircleIcon" size={24} />
            <span>{message}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="max-w-md mx-auto space-y-3">
            <div className="flex flex-col sm:flex-row gap-4">
              <label htmlFor="newsletter-email" className="sr-only">
                Email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (hasError) setStatus('idle');
                }}
                placeholder="your@email.com"
                autoComplete="email"
                required
                disabled={submitting}
                aria-invalid={hasError}
                aria-describedby={hasError ? 'newsletter-error' : undefined}
                className={`flex-1 px-6 py-3 bg-background border rounded-md font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition duration-300 disabled:opacity-60 ${
                  hasError ? 'border-error focus:ring-error/40' : 'border-border focus:ring-primary'
                }`}
              />

              {/* Honeypot  - visually hidden, off-screen, ignored by humans */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="sr-only"
              />

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary text-primary-foreground font-cta font-semibold rounded-md hover:bg-primary/90 hover:-translate-y-0.5 hover:shadow-dramatic transition duration-300 active:scale-[0.98] active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none disabled:active:scale-100"
              >
                {submitting && (
                  <Icon
                    name="ArrowPathIcon"
                    size={18}
                    className="animate-spin text-primary-foreground"
                  />
                )}
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
            </div>

            {hasError && (
              <p
                id="newsletter-error"
                role="alert"
                className="font-body text-sm text-error text-left sm:text-center"
              >
                {message}
              </p>
            )}
          </form>
        )}

        <p className="font-body text-xs text-muted-foreground">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </div>
    </div>
  );
};

export default NewsletterSignup;
